<template>
  <div class="min-h-screen bg-gray-50">
    <!-- ヘッダー（サンプルモード/通常モードで切り替え） -->
    <header class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <span v-if="isEmbedMode" class="text-2xl font-bold text-gray-900">Soarimetric</span>
            <router-link v-else :to="isSampleMode ? '/' : '/dashboard'" class="text-2xl font-bold text-gray-900">
              Soarimetric
            </router-link>
          </div>
          <nav v-if="!isSampleMode && !isEmbedMode" class="flex items-center space-x-4">
            <router-link to="/dashboard" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              ダッシュボード
            </router-link>
            <router-link to="/areas" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              エリア管理
            </router-link>
          </nav>
          <nav v-else-if="!isEmbedMode" class="flex items-center space-x-1 sm:space-x-3">
            <router-link
              to="/"
              class="bg-blue-600 text-white px-2 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-blue-700 transition text-xs sm:text-sm font-semibold shadow-md whitespace-nowrap"
            >
              <span class="hidden sm:inline">← トップページへ戻る</span>
              <span class="sm:hidden">← トップ</span>
            </router-link>
            <router-link
              to="/register"
              class="bg-green-600 text-white px-2 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 transition text-xs sm:text-sm whitespace-nowrap"
            >
              <span class="hidden sm:inline">無料で登録</span>
              <span class="sm:hidden">登録</span>
            </router-link>
            <router-link
              to="/login"
              class="bg-indigo-600 text-white px-2 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-indigo-700 transition text-xs sm:text-sm whitespace-nowrap"
            >
              ログイン
            </router-link>
          </nav>
        </div>
      </div>
    </header>

    <!-- 過去モードバナー（スクロールしても常に表示される目印） -->
    <div
      v-if="isHistoryMode"
      class="sticky top-0 bg-amber-500 text-white shadow-md"
      style="z-index: 1000"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 text-sm font-semibold">
          <span>🕘</span>
          <span>過去の気象情報を表示中</span>
          <span v-if="selectedDate" class="hidden sm:inline font-normal">— {{ formatDate(selectedDate) }}</span>
        </div>
        <button
          @click="exitHistoryMode"
          :disabled="isRefreshing"
          class="bg-white/20 hover:bg-white/30 disabled:opacity-50 px-3 py-1 rounded-md text-xs font-semibold whitespace-nowrap"
        >
          未来の気象情報を表示
        </button>
      </div>
    </div>

    <!-- サンプル通知バナー（サンプルモード時のみ表示） -->
    <!-- コメントアウト: 将来復活させる可能性あり
    <div v-if="isSampleMode" class="bg-yellow-50 border-b border-yellow-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div class="flex items-center">
          <svg class="h-5 w-5 text-yellow-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm text-yellow-700">
            これはサンプル表示です。全20エリアの詳細情報を見るには
            <router-link to="/register" class="font-medium underline">無料登録</router-link>
            してください。
          </p>
        </div>
      </div>
    </div>
    -->

    <main class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        
        <div v-if="isLoading" class="text-center py-12">
          <div class="text-4xl mb-4">⏳</div>
          <p class="text-gray-500">気象データを読み込み中...</p>
        </div>

        <div v-else-if="weatherData">
          <!-- エリア情報（地図表示） -->
          <div class="bg-white shadow rounded-lg mb-6" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
            <div class="px-4 py-5 sm:p-6">
              <h1 class="text-3xl font-bold text-gray-900 mb-4">{{ weatherData.area_name }}の詳細気象データ<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h1>
              <!-- 免責事項。文言と出典表記は Android アプリ（ForecastPage / ChartsPage）と揃えてある。
                   埋め込みモード（?embed=1）ではヘッダーのリンクが無く、ここ以外に
                   免責事項へ到達する経路が無いため、モードによらず必ず表示する -->
              <div class="mb-4 border-l-4 border-gray-300 bg-gray-50 pl-3 pr-2 py-2 text-xs text-gray-500 leading-relaxed">
                <p>
                  <span class="font-semibold text-gray-600">気象データ: NOAA/NWS GFS</span>
                  — 数値予報モデルの計算結果をそのまま表示するものであり、内容の正確性を保証するものではありません。フライトの可否判断はパイロット自身の責任で行ってください。
                </p>
                <p class="mt-1">正式な気象情報は気象庁の発表をご確認ください。</p>
              </div>

              <div class="map-container" style="height: 300px; border-radius: 8px; overflow: hidden;">
                <div ref="mapContainer" style="height: 100%; width: 100%;"></div>
              </div>
            </div>
          </div>

          <!-- 日付選択 -->
          <div :class="['shadow rounded-lg mb-6', isHistoryMode ? 'bg-amber-50 border-2 border-amber-400' : 'bg-white']">
            <div class="px-4 py-5 sm:p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-gray-900">
                  日付選択
                  <span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-500 text-white text-xs font-bold">過去</span>
                </h3>
                <div class="flex items-center gap-3">
                  <div class="text-sm">
                    <div v-if="isHistoryMode" class="text-amber-700 font-medium">
                      当時の最新予報
                    </div>
                    <div v-else class="text-blue-600 font-medium">
                      {{ weatherData.model_run ? `計算時刻: ${formatModelRun(weatherData.model_run)}` : `更新: ${formatDateTime(weatherData.last_updated)}` }}
                    </div>
                    <div v-if="weatherData.from_cache" class="text-xs text-gray-500">
                      {{ weatherData.data_age_hours }}時間前のデータ
                    </div>
                  </div>
                  <button
                    @click="refreshWeatherData"
                    :disabled="isRefreshing"
                    class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <span v-if="isRefreshing">🔄</span>
                    <span v-else>🔄</span>
                    <span>{{ isRefreshing ? '更新中...' : '更新' }}</span>
                  </button>
                </div>
              </div>
              <!-- 過去／未来モードの切り替え -->
              <div class="mb-4 flex flex-wrap items-center gap-3">
                <button
                  v-if="!isHistoryMode"
                  @click="enterHistoryMode"
                  :disabled="isRefreshing"
                  class="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {{ isRefreshing ? '読み込み中…' : '🕘 過去の気象情報を表示' }}
                </button>
                <template v-else>
                  <button
                    @click="exitHistoryMode"
                    :disabled="isRefreshing"
                    class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    ☀️ 未来の気象情報を表示
                  </button>
                  <label class="flex items-center gap-2 text-sm text-amber-900 font-medium">
                    表示する日付
                    <input
                      type="date"
                      v-model="historyDate"
                      :min="historyMinDate"
                      :max="historyMaxDate"
                      :disabled="isRefreshing"
                      @change="onHistoryDateChange"
                      class="border border-amber-400 rounded-md px-2 py-1 text-sm bg-white"
                    />
                  </label>
                  <span v-if="availableDates.length" class="text-xs text-amber-700">
                    {{ historyMinDate }} 〜 {{ historyMaxDate }}
                    の全{{ availableDates.length }}日分を表示中
                  </span>
                </template>
              </div>

              <p v-if="historyError" class="mb-4 text-sm text-red-600">{{ historyError }}</p>

              <div class="overflow-x-auto" ref="historyDateRow">
                <table class="w-full">
                  <tbody>
                    <tr>
                      <td 
                        v-for="date in availableDates" 
                        :key="date" 
                        :class="dateCellClass(date)"
                        @click="selectDate(date)"
                      >
                        <div class="text-sm font-medium mb-1">{{ formatDateShort(date) }}</div>
                        <div class="text-xs text-gray-500 mb-2">{{ formatDayOfWeek(date) }}</div>
                        <div class="text-lg mb-2">{{ getDailyWeatherIcon(date) }}</div>
                        <div class="text-xs">
                          <div class="text-red-600 font-medium">{{ getDailyMaxTemp(date) }}°</div>
                          <div class="text-blue-600 font-medium">{{ getDailyMinTemp(date) }}°</div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- 基本気象情報(1時間ごとのテーブル形式) -->
          <div class="bg-white shadow rounded-lg mb-6" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">基本気象情報<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
              
              <div class="overflow-x-auto">
                <table class="w-auto">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-16 whitespace-nowrap">項目</th>
                      <th 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="forecast.time"
                        :class="[
                          'px-2 py-2 text-center text-xs font-medium tracking-wider cursor-pointer w-16',
                          selectedTimeIndex === index ? 'bg-blue-100 text-blue-800' : 'text-gray-500 hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        <div class="text-xs">{{ formatTime(forecast.time) }}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200 text-xs">
                    <!-- 天気 -->
                    <tr>
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10 min-w-16 whitespace-nowrap">天気</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`weather-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        <div class="text-lg">{{ getWeatherIcon(forecast.surface.weather_code) }}</div>
                      </td>
                    </tr>
                    <!-- 気温 -->
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-16 whitespace-nowrap">気温</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`temp-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center font-medium text-red-600 cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        {{ Math.round(forecast.surface.temperature) }}°
                      </td>
                    </tr>
                    <!-- 降水確率 -->
                    <tr>
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10 min-w-16 whitespace-nowrap">降水確率</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`precip-prob-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center font-medium text-blue-600 cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        {{ forecast.surface.precipitation_probability != null ? forecast.surface.precipitation_probability + '%' : '-' }}
                      </td>
                    </tr>
                    <!-- 降水量 -->
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-16 whitespace-nowrap">降水量</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`precip-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center font-medium text-green-600 cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        {{ forecast.surface.precipitation || 0 }}mm
                      </td>
                    </tr>
                    <!-- 雲量 -->
                    <tr>
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10 min-w-16 whitespace-nowrap">雲量</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`cloud-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        {{ forecast.surface.cloud_cover }}%
                      </td>
                    </tr>
                    <!-- 風向・風速 -->
                    <tr class="bg-gray-50">
                      <td class="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-16 whitespace-nowrap">風</td>
                      <td 
                        v-for="(forecast, index) in dailyForecast" 
                        :key="`wind-${forecast.time}`"
                        :class="[
                          'px-1 py-2 text-center cursor-pointer',
                          selectedTimeIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'
                        ]"
                        @click="selectTimeForAltitudeData(index)"
                      >
                        <div class="flex flex-col items-center space-y-1">
                          <div class="wind-arrow scale-75" :style="getWindArrowStyle(forecast.surface.wind_direction, forecast.surface.wind_speed)"></div>
                          <div class="text-xs font-medium text-blue-600">{{ Math.round(forecast.surface.wind_speed) }}m/s</div>
                          <div v-if="forecast.surface.wind_gusts" class="text-xs font-medium text-red-600">{{ Math.round(Math.max(forecast.surface.wind_gusts, forecast.surface.wind_speed)) }}m/s</div>
                          <div class="text-xs text-gray-500">{{ getWindDirection(forecast.surface.wind_direction) }}</div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- 基本気象情報(時系列グラフ) -->
          <div class="bg-white shadow rounded-lg mb-6" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">基本気象情報（時系列グラフ）<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
              
              <!-- グラフ切り替えタブ -->
              <div class="mb-4">
                <div class="overflow-x-auto">
                  <nav class="flex space-x-2 sm:space-x-4 min-w-max pb-2">
                    <button
                      v-for="tab in chartTabs"
                      :key="tab.id"
                      @click="activeChartTab = tab.id"
                      :class="[
                        activeChartTab === tab.id
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800',
                        'whitespace-nowrap py-2 px-3 border rounded-lg font-medium text-xs sm:text-sm transition-colors flex-shrink-0'
                      ]"
                    >
                      {{ tab.label }}
                    </button>
                  </nav>
                </div>
              </div>

              <div class="h-80">
                <canvas :ref="'timeSeriesChart'"></canvas>
              </div>
            </div>
          </div>

          <!-- 高度別雲量カラーマップ -->
          <div class="bg-white shadow rounded-lg mb-6" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">高度別雲量<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
              <div class="cloud-colormap-container">
                <canvas ref="cloudColormapCanvas"></canvas>
              </div>
            </div>
          </div>

          <!-- 時間選択 -->
          <div class="bg-white shadow rounded-lg mb-6" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
            <div class="px-4 py-3 sm:px-6">
              <h3 class="text-base font-medium text-gray-900 mb-2">高度別データ表示時間選択<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
              <div class="flex items-center justify-between">
                <button
                  @click="previousTime"
                  :disabled="selectedTimeIndex <= 0 && availableDates.indexOf(selectedDate) <= 0"
                  class="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-1 text-sm"
                >
                  <span>←</span>
                  <span>戻る</span>
                </button>

                <div class="text-center flex items-center justify-center space-x-2">
                  <span class="text-sm font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {{ formatDateShort(dailyForecast[selectedTimeIndex]?.time) }}
                    ({{ formatDayOfWeek(dailyForecast[selectedTimeIndex]?.time) }})
                  </span>
                  <span class="text-lg font-bold text-blue-600">
                    {{ formatTime(dailyForecast[selectedTimeIndex]?.time) }}
                  </span>
                  <span class="text-base font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                    {{ dailyForecast[selectedTimeIndex]?.surface.temperature.toFixed(1) }}°C
                  </span>
                </div>

                <button
                  @click="nextTime"
                  :disabled="selectedTimeIndex >= dailyForecast.length - 1 && availableDates.indexOf(selectedDate) >= availableDates.length - 1"
                  class="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-1 text-sm"
                >
                  <span>進む</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 高度別気象情報 -->
          <div v-if="selectedForecast" class="space-y-6">
            
            <!-- エマグラム（高度毎の気温・露点温度）-->
            <div class="bg-white shadow rounded-lg" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
              <div class="px-4 py-5 sm:p-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">エマグラム（高度毎の気温・露点温度）<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
                <div class="emagram-container">
                  <canvas ref="emagramChart"></canvas>
                </div>
              </div>
            </div>

            <!-- ホドグラフ -->
            <div class="bg-white shadow rounded-lg" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
              <div class="px-4 py-5 sm:p-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">ホドグラフ（高度毎の風向風速ベクトル）<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
                <div class="hodograph-container">
                  <canvas ref="hodographChart"></canvas>
                </div>
              </div>
            </div>

            <!-- 高度別気象データ（統合テーブル）-->
            <div class="bg-white shadow rounded-lg" :class="isHistoryMode ? 'border-l-4 border-amber-400' : ''">
              <div class="px-4 py-5 sm:p-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">高度別気象データ<span v-if="isHistoryMode" class="ml-2 align-middle inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300">過去</span></h3>
                
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">気圧</th>
                        <th class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">高度</th>
                        <th class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">風</th>
                        <th class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">気温</th>
                        <th class="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">雲量</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr 
                        v-for="(level, index) in reversedPressureLevels" 
                        :key="level"
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                      >
                        <td class="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 text-center">
                          {{ level }}hPa
                        </td>
                        <td class="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">
                          {{ selectedForecast.pressure_levels?.[`${level}hPa`]?.geopotential_height || 'N/A' }}m
                        </td>
                        <td class="px-2 py-1 whitespace-nowrap text-xs text-gray-900">
                          <div class="flex flex-col items-center space-y-0">
                            <!-- 風向矢印 -->
                            <div class="wind-arrow scale-75" :style="getWindArrowStyle(selectedForecast.pressure_levels?.[`${level}hPa`]?.wind_direction, selectedForecast.pressure_levels?.[`${level}hPa`]?.wind_speed)">
                            </div>
                            <div class="text-center">
                              <div class="text-xs font-medium text-blue-600">{{ Math.round(selectedForecast.pressure_levels?.[`${level}hPa`]?.wind_speed || 0) }}m/s</div>
                              <div class="text-xs text-gray-500">{{ getWindDirection(selectedForecast.pressure_levels?.[`${level}hPa`]?.wind_direction) }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="px-2 py-1 whitespace-nowrap text-xs text-center text-red-600 font-medium">
                          {{ (selectedForecast.pressure_levels?.[`${level}hPa`]?.temperature || 0).toFixed(1) }}°
                        </td>
                        <td class="px-2 py-1 whitespace-nowrap text-xs text-center text-gray-600 font-medium">
                          {{ selectedForecast.pressure_levels?.[`${level}hPa`]?.cloud_cover || 0 }}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div v-else class="text-center py-12">
          <div class="text-4xl mb-4">⚠️</div>
          <p class="text-gray-500">{{ historyError || '気象データの読み込みに失敗しました' }}</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useWeatherStore } from '@/stores/weather'
import axios from 'axios'
import Chart from 'chart.js/auto'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const API_BASE_URL = process.env.VUE_APP_API_URL || ''

export default {
  name: 'WeatherDetail',
  setup() {
    const route = useRoute()
    const weatherStore = useWeatherStore()

    // サンプルモード判定（ルート名で判断）
    const isSampleMode = computed(() => route.name === 'SampleDetail')

    // 埋め込みモード判定（?embed=1）
    // ヘッダーのメニュー（トップへ戻る/登録/ログイン）を隠し、
    // エリアの気象情報だけを見せるためのURL。リンク配布用。
    const isEmbedMode = computed(() => route.query.embed === '1')
    
    const weatherData = ref(null)
    const selectedDate = ref('')
    const selectedTimeIndex = ref(0)
    const isLoading = ref(false)
    const isRefreshing = ref(false)
    const activeChartTab = ref('temperature')

    // --- 過去の気象情報モード ---
    // 既定は未来（通常）モード。過去モードでは /api/app/history/ を参照する。
    const isHistoryMode = ref(false)
    const historyDate = ref('')            // 日付ピッカーの値（YYYY-MM-DD）
    const historyError = ref('')
    const historyDateRow = ref(null)       // 日付セル行（選択日までスクロールする）
    
    // Chart references
    const timeSeriesChart = ref(null)
    const emagramChart = ref(null)
    const hodographChart = ref(null)
    const cloudColormapCanvas = ref(null)

    // Map reference
    const mapContainer = ref(null)
    let mapInstance = null
    
    // Chart instances
    let timeSeriesChartInstance = null
    let emagramChartInstance = null
    let hodographChartInstance = null

    const pressureLevels = [1000, 975, 950, 925, 900, 850, 800, 750, 700]
    
    // 高度順（高い高度が上）に並べるために気圧レベルを逆順に
    const reversedPressureLevels = computed(() => {
      return [...pressureLevels].reverse()
    })

    const chartTabs = [
      { id: 'temperature', label: '気温(地上)' },
      { id: 'precipitation_probability', label: '降水確率' },
      { id: 'precipitation', label: '降水量' },
      { id: 'cloud_cover', label: '雲カバー率(地上)' },
      { id: 'wind_speed', label: '風速(地上)' },
      { id: 'pressure', label: '気圧(海面)' },
      { id: 'visibility', label: '視程(地上)' },
      { id: 'boundary_layer', label: '境界層高度' }
    ]
    
    const availableDates = computed(() => {
      if (!weatherData.value?.forecast) return []
      
      const dates = new Set()
      weatherData.value.forecast.forEach(forecast => {
        const date = new Date(forecast.time).toDateString()
        dates.add(date)
      })
      return Array.from(dates).sort((a, b) => new Date(a) - new Date(b))
    })

    const dailyForecast = computed(() => {
      if (!weatherData.value?.forecast || !selectedDate.value) return []
      
      return weatherData.value.forecast.filter(forecast => {
        const forecastDate = new Date(forecast.time).toDateString()
        return forecastDate === selectedDate.value
      })
    })

    const selectedForecast = computed(() => {
      if (!dailyForecast.value.length) return null
      return dailyForecast.value[selectedTimeIndex.value]
    })

    const loadWeatherData = async () => {
      // 更新時はisLoadingをtrueにしない（DOM要素を保持するため）
      const isInitialLoad = !weatherData.value

      if (isInitialLoad) {
        isLoading.value = true
      }

      try {
        // サンプルモードと通常モードでパラメータ名とAPIエンドポイントが異なる
        const areaId = isSampleMode.value ? route.params.id : route.params.areaId

        if (isSampleMode.value) {
          // サンプルモード: axios直接呼び出し
          const response = await axios.get(`${API_BASE_URL}/api/weather/sample/forecast/${areaId}/`)
          weatherData.value = response.data
        } else {
          // 通常モード: weatherStore経由
          const data = await weatherStore.fetchWeatherForecast(areaId)
          weatherData.value = data
        }

        // デフォルトで今日の日付を選択
        if (availableDates.value.length > 0) {
          selectedDate.value = availableDates.value[0]
          selectDefaultTimeIndex()
        }

        // DOMが更新されるまで待機
        await nextTick()

        // チャート初期化（遅延を入れてDOM準備を待つ）
        setTimeout(() => {
          createTimeSeriesChart()
          drawCloudColormap()
          // 高度別チャートも初期化
          updateAltitudeCharts()
        }, 150)

      } catch (error) {
        console.error('Weather data fetch failed:', error)
      } finally {
        if (isInitialLoad) {
          isLoading.value = false
        }
      }
    }

    const selectDefaultTimeIndex = () => {
      // 12時に最も近い時刻を選択
      const forecasts = dailyForecast.value
      if (!forecasts.length) {
        selectedTimeIndex.value = 0
        return
      }
      const noonIndex = forecasts.findIndex(f => {
        const hour = new Date(f.time).getHours()
        return hour >= 12
      })
      selectedTimeIndex.value = noonIndex >= 0 ? noonIndex : 0
    }

    const selectDate = (date) => {
      selectedDate.value = date
      if (isHistoryMode.value) historyDate.value = toIsoDate(date)
      selectDefaultTimeIndex()
      createTimeSeriesChart()
      drawCloudColormap()
    }

    const filterForecastByDate = () => {
      selectDefaultTimeIndex()
      createTimeSeriesChart()
      drawCloudColormap()
    }

    const selectTimeForAltitudeData = (index) => {
      selectedTimeIndex.value = index
      updateAltitudeCharts()
    }

    const previousTime = () => {
      if (selectedTimeIndex.value > 0) {
        // 同じ日付内で前の時刻に移動
        selectedTimeIndex.value -= 1
        updateAltitudeCharts()
      } else {
        // 0時より前に戻る場合、前の日付に切り替え
        const currentDateIndex = availableDates.value.indexOf(selectedDate.value)
        if (currentDateIndex > 0) {
          // 前の日付が存在する場合
          selectedDate.value = availableDates.value[currentDateIndex - 1]
          // 前の日付の最後の時刻に移動
          const prevDayForecast = weatherData.value.forecast.filter(forecast => {
            const forecastDate = new Date(forecast.time).toDateString()
            return forecastDate === selectedDate.value
          })
          selectedTimeIndex.value = prevDayForecast.length - 1
          createTimeSeriesChart()
          updateAltitudeCharts()
        }
      }
    }

    const nextTime = () => {
      if (selectedTimeIndex.value < dailyForecast.value.length - 1) {
        // 同じ日付内で次の時刻に移動
        selectedTimeIndex.value += 1
        updateAltitudeCharts()
      } else {
        // 23時より後に進む場合、次の日付に切り替え
        const currentDateIndex = availableDates.value.indexOf(selectedDate.value)
        if (currentDateIndex < availableDates.value.length - 1) {
          // 次の日付が存在する場合
          selectedDate.value = availableDates.value[currentDateIndex + 1]
          // 次の日付の最初の時刻（0時）に移動
          selectedTimeIndex.value = 0
          createTimeSeriesChart()
          updateAltitudeCharts()
        }
      }
    }

    const formatDateTime = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatModelRun = (modelRun) => {
      const match = modelRun.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{1,2})Z$/)
      if (!match) return modelRun
      const utc = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4]))
      return `${utc.getMonth() + 1}/${utc.getDate()} ${String(utc.getHours()).padStart(2, '0')}:00`
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short'
      })
    }

    const formatDateShort = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('ja-JP', {
        month: 'numeric',
        day: 'numeric'
      })
    }

    const formatDayOfWeek = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('ja-JP', {
        weekday: 'short'
      })
    }

    const getDailyWeatherIcon = (dateString) => {
      if (!weatherData.value?.forecast) return ''
      
      const dailyForecasts = weatherData.value.forecast.filter(forecast => {
        const forecastDate = new Date(forecast.time).toDateString()
        return forecastDate === dateString
      })
      
      if (dailyForecasts.length === 0) return ''
      
      // 日中の時間帯（6時〜18時）の天気コードを優先
      const daytimeForecasts = dailyForecasts.filter(forecast => {
        const hour = new Date(forecast.time).getHours()
        return hour >= 6 && hour <= 18
      })
      
      const representativeForecast = daytimeForecasts.length > 0 
        ? daytimeForecasts[Math.floor(daytimeForecasts.length / 2)] 
        : dailyForecasts[0]
      
      return getWeatherIcon(representativeForecast.surface.weather_code)
    }

    const getDailyMaxTemp = (dateString) => {
      if (!weatherData.value?.forecast) return 0
      
      const dailyForecasts = weatherData.value.forecast.filter(forecast => {
        const forecastDate = new Date(forecast.time).toDateString()
        return forecastDate === dateString
      })
      
      if (dailyForecasts.length === 0) return 0
      
      const temps = dailyForecasts.map(f => f.surface.temperature)
      return Math.round(Math.max(...temps))
    }

    const getDailyMinTemp = (dateString) => {
      if (!weatherData.value?.forecast) return 0
      
      const dailyForecasts = weatherData.value.forecast.filter(forecast => {
        const forecastDate = new Date(forecast.time).toDateString()
        return forecastDate === dateString
      })
      
      if (dailyForecasts.length === 0) return 0
      
      const temps = dailyForecasts.map(f => f.surface.temperature)
      return Math.round(Math.min(...temps))
    }

    const formatTime = (timeString) => {
      const date = new Date(timeString)
      return date.toLocaleString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getWeatherIcon = (code) => {
      if (code === 0) return '☀️'        // 快晴
      if (code === 1) return '🌤️'        // 概ね晴れ
      if (code === 2) return '⛅'        // 部分的に曇り
      if (code === 3) return '☁️'        // 曇り
      if (code >= 45 && code <= 48) return '🌫️'  // 霧
      if (code >= 51 && code <= 67) return '🌧️'  // 雨
      if (code >= 71 && code <= 77) return '🌨️'  // 雪
      if (code >= 80 && code <= 82) return '🌦️'  // にわか雨
      if (code >= 85 && code <= 86) return '🌨️'  // にわか雪
      if (code >= 95 && code <= 99) return '⛈️'  // 雷雨
      return '☁️'                        // デフォルト
    }

    const getWeatherCodeDescription = (code) => {
      const weatherCodes = {
        0: '快晴',
        1: '概ね晴れ',
        2: '部分的に曇り',
        3: '曇り',
        45: '霧',
        48: '着氷霧',
        51: '軽い霧雨',
        53: '霧雨',
        55: '濃い霧雨',
        56: '軽い凍結霧雨',
        57: '濃い凍結霧雨',
        61: '小雨',
        63: '雨',
        65: '大雨',
        66: '軽い凍結雨',
        67: '激しい凍結雨',
        71: '小雪',
        73: '雪',
        75: '大雪',
        77: '細雪',
        80: '軽いにわか雨',
        81: 'にわか雨',
        82: '激しいにわか雨',
        85: '軽いにわか雪',
        86: '激しいにわか雪',
        95: '雷雨',
        96: '軽い雹を伴う雷雨',
        99: '激しい雹を伴う雷雨'
      }
      return weatherCodes[code] || '不明'
    }

    const getWindDirection = (degree) => {
      if (degree == null) return '-'
      const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
      const index = Math.round(degree / 22.5) % 16
      return directions[index]
    }

    const getWindArrowStyle = (windDirection, windSpeed) => {
      if (!windDirection || !windSpeed) {
        return {
          transform: 'rotate(0deg)',
          opacity: '0.3',
          '--arrow-color': '#9CA3AF',
          '--arrow-size': '1'
        }
      }

      // 風向を矢印の向きに変換（風が吹いてくる方向なので180度回転、さらに90度調整）
      const rotation = (windDirection + 180 - 90) % 360
      
      // 風速に応じて矢印のサイズと色を変更
      let arrowSize = '1'
      let color = '#9CA3AF'
      
      if (windSpeed >= 10) {
        arrowSize = '1.0'
        color = '#DC2626' // 強風（赤）
      } else if (windSpeed >= 6) {
        arrowSize = '1.0'
        color = '#EA580C' // 強め（オレンジ）
      } else if (windSpeed >= 4) {
        arrowSize = '1.0'
        color = '#0891B2' // 中程度（青）
      } else if (windSpeed >= 2) {
        arrowSize = '1.0'
        color = '#059669' // 弱い（緑）
      }

      return {
        transform: `rotate(${rotation}deg)`,
        '--arrow-color': color,
        '--arrow-size': arrowSize,
        transition: 'all 0.2s ease'
      }
    }

    const getCloudLevel = (cloudCover) => {
      if (cloudCover <= 10) return '快晴'
      if (cloudCover <= 25) return '晴れ'
      if (cloudCover <= 75) return '曇り'
      return '曇天'
    }

    const createTimeSeriesChart = () => {
      if (timeSeriesChartInstance) {
        timeSeriesChartInstance.destroy()
      }

      if (!dailyForecast.value.length) return

      const ctx = timeSeriesChart.value?.getContext('2d')
      if (!ctx) return

      const labels = dailyForecast.value.map(f => formatTime(f.time))
      let data, label, color, unit

      switch (activeChartTab.value) {
        case 'temperature':
          data = dailyForecast.value.map(f => parseFloat(f.surface.temperature.toFixed(1)))
          label = '気温(地上)'
          color = 'rgb(239, 68, 68)'
          unit = '°C'
          break
        case 'precipitation_probability':
          data = dailyForecast.value.map(f => f.surface.precipitation_probability)
          label = '降水確率'
          color = 'rgb(59, 130, 246)'
          unit = '%'
          break
        case 'precipitation':
          data = dailyForecast.value.map(f => f.surface.precipitation || 0)
          label = '降水量'
          color = 'rgb(16, 185, 129)'
          unit = 'mm'
          break
        case 'cloud_cover':
          data = dailyForecast.value.map(f => f.surface.cloud_cover)
          label = '雲カバー率(地上)'
          color = 'rgb(156, 163, 175)'
          unit = '%'
          break
        case 'wind_speed':
          data = dailyForecast.value.map(f => parseFloat(f.surface.wind_speed.toFixed(1)))
          label = '風速(地上)'
          color = 'rgb(34, 197, 94)'
          unit = 'm/s'
          break
        case 'pressure':
          data = dailyForecast.value.map(f => Math.round(f.surface.pressure_msl || f.surface.pressure))
          label = '気圧(海面)'
          color = 'rgb(168, 85, 247)'
          unit = 'hPa'
          break
        case 'visibility':
          data = dailyForecast.value.map(f => Math.round((f.surface.visibility || 15000) / 1000))
          label = '視程(地上)'
          color = 'rgb(245, 158, 11)'
          unit = 'km'
          break
        case 'boundary_layer':
          data = dailyForecast.value.map(f => f.surface.boundary_layer_height != null ? Math.round(f.surface.boundary_layer_height) : null)
          label = '境界層高度'
          color = 'rgb(139, 92, 246)'
          unit = 'm'
          break
        default:
          return
      }

      // 風速チャートの場合はガスト線も追加
      const datasets = [{
        label,
        data,
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        // 選択中のポイントを目立たせる
        pointRadius: data.map((_, index) =>
          index === selectedTimeIndex.value ? 5 : 3
        ),
        pointBackgroundColor: data.map((_, index) =>
          index === selectedTimeIndex.value ? 'rgb(255, 193, 7)' : color
        ),
        pointBorderColor: data.map((_, index) =>
          index === selectedTimeIndex.value ? 'rgb(234, 179, 8)' : color
        ),
        pointBorderWidth: data.map((_, index) =>
          index === selectedTimeIndex.value ? 2 : 1
        ),
        pointHoverRadius: data.map((_, index) =>
          index === selectedTimeIndex.value ? 7 : 5
        )
      }]

      if (activeChartTab.value === 'wind_speed') {
        const gustData = dailyForecast.value.map(f =>
          f.surface.wind_gusts ?
            parseFloat(Math.max(f.surface.wind_gusts, f.surface.wind_speed).toFixed(1)) :
            parseFloat(f.surface.wind_speed.toFixed(1))
        )
        datasets.push({
          label: 'ガスト風速',
          data: gustData,
          borderColor: 'rgb(234, 88, 12)', // オレンジ色
          backgroundColor: 'rgba(234, 88, 12, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5], // 破線
          fill: false,
          tension: 0.4,
          // ガスト線も選択中のポイントを目立たせる
          pointRadius: gustData.map((_, index) =>
            index === selectedTimeIndex.value ? 8 : 3
          ),
          pointBackgroundColor: gustData.map((_, index) =>
            index === selectedTimeIndex.value ? 'rgb(255, 193, 7)' : 'rgb(234, 88, 12)'
          ),
          pointBorderColor: gustData.map((_, index) =>
            index === selectedTimeIndex.value ? 'rgb(234, 179, 8)' : 'rgb(234, 88, 12)'
          ),
          pointBorderWidth: gustData.map((_, index) =>
            index === selectedTimeIndex.value ? 3 : 1
          ),
          pointHoverRadius: gustData.map((_, index) =>
            index === selectedTimeIndex.value ? 10 : 5
          )
        })
      }

      // 選択中の時刻に垂直線を描画するプラグイン
      const verticalLinePlugin = {
        id: 'verticalLinePlugin',
        afterDraw: (chart) => {
          if (selectedTimeIndex.value >= 0 && selectedTimeIndex.value < chart.data.labels.length) {
            const ctx = chart.ctx
            const xScale = chart.scales.x
            const yScale = chart.scales.y

            // X軸のラベル位置を取得
            const x = xScale.getPixelForValue(selectedTimeIndex.value)
            const yTop = yScale.top
            const yBottom = yScale.bottom

            ctx.save()
            ctx.beginPath()
            ctx.moveTo(x, yTop)
            ctx.lineTo(x, yBottom)
            ctx.lineWidth = 2
            ctx.strokeStyle = 'rgba(255, 193, 7, 0.5)' // 黄色の半透明
            ctx.setLineDash([5, 5])
            ctx.stroke()
            ctx.restore()
          }
        }
      }

      timeSeriesChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: datasets
        },
        plugins: [verticalLinePlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,  // アニメーションを無効化してちらつきを防止
          onClick: (event, elements) => {
            // グラフ上の任意の場所をクリックした場合
            if (elements.length > 0) {
              const clickedIndex = elements[0].index
              selectTimeForAltitudeData(clickedIndex)
            }
          },
          scales: {
            x: {
              title: { display: true, text: '時刻' }
            },
            y: {
              title: { display: true, text: `${label} (${unit})` },
              ...(activeChartTab.value === 'precipitation_probability' || activeChartTab.value === 'cloud_cover' ? {
                min: 0,
                max: 100
              } : activeChartTab.value === 'visibility' ? {
                min: 0,
                max: 25
              } : activeChartTab.value === 'pressure' ? {
                min: Math.min(...data) - Math.max(5, (Math.max(...data) - Math.min(...data)) / 2),
                max: Math.max(...data) + Math.max(5, (Math.max(...data) - Math.min(...data)) / 2)
              } : (activeChartTab.value === 'precipitation' || activeChartTab.value === 'wind_speed') ? {
                min: 0
              } : {})
            }
          },
          plugins: {
            legend: { display: true },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.parsed.y}${unit}`
                }
              }
            }
          }
        }
      })
    }

    // 同じ日の全時刻データからX軸範囲を計算する関数
    const calculateEmagramXAxisRange = () => {
      if (!dailyForecast.value || dailyForecast.value.length === 0) return { min: -20, max: 35 }

      // 同じ日の全時刻のデータから最大・最小温度を取得
      const allTemperatures = []

      dailyForecast.value.forEach(forecast => {
        // 各気圧レベルの温度と露点温度
        pressureLevels.forEach(level => {
          const levelKey = `${level}hPa`
          const temp = forecast.pressure_levels?.[levelKey]?.temperature
          const dewPoint = forecast.pressure_levels?.[levelKey]?.dew_point
          if (temp != null && temp !== 0) allTemperatures.push(temp)
          if (dewPoint != null && dewPoint !== 0) allTemperatures.push(dewPoint)
        })

        // 地表温度と露点温度
        const surfaceTemp = forecast.surface?.temperature
        const surfaceDewPoint = forecast.surface?.dew_point
        if (surfaceTemp != null) allTemperatures.push(surfaceTemp)
        if (surfaceDewPoint != null) allTemperatures.push(surfaceDewPoint)
      })

      if (allTemperatures.length === 0) return { min: -20, max: 35 }

      const maxTemp = Math.max(...allTemperatures)

      // 最高温度を基準に範囲を設定（5の倍数に調整）
      const rawMin = maxTemp - 22.5
      const rawMax = maxTemp + 2.5
      const xAxisMin = Math.floor(rawMin / 5) * 5
      const xAxisMax = Math.ceil(rawMax / 5) * 5

      return { min: xAxisMin, max: xAxisMax }
    }

    const createEmagramChart = () => {
      if (!selectedForecast.value) return

      const ctx = emagramChart.value?.getContext('2d')
      if (!ctx) return

      // 既存のチャートがある場合は、データのみ更新
      if (emagramChartInstance) {
        updateEmagramChartData()
        return
      }

      const heights = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
      })

      const temperatures = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.temperature || 0
      })

      const dewPoints = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.dew_point || 0
      })

      // データポイントを作成（X軸：気温、Y軸：高度）
      const temperatureData = temperatures.map((temp, index) => ({
        x: temp,
        y: heights[index]
      }))

      const dewPointData = dewPoints.map((dewPoint, index) => ({
        x: dewPoint,
        y: heights[index]
      }))

      // 地表データ（surface_elevation + 2m高度）を追加
      const surfaceTemp = selectedForecast.value.surface?.temperature || null
      const surfaceDewPoint = selectedForecast.value.surface?.dew_point || null
      // 地表の気温・露点が基準にしている実標高(DEM)。model_elevation は気象モデルの
      // 地形標高で平滑化されており実標高とずれる（獅子吼: 実標高147m に対し340.4m）。
      // surface_elevation を返さない旧バックエンドには従来値でフォールバックする。
      const baseElevation = selectedForecast.value.surface_elevation ??
                            (selectedForecast.value.model_elevation || 0)
      const surfaceElevation = baseElevation + 2 // 地表面 + 2m

      // 同じ日の全時刻データからX軸範囲を計算（日付が変わらない限り固定）
      const xAxisRange = calculateEmagramXAxisRange()
      const xAxisMin = xAxisRange.min
      const xAxisMax = xAxisRange.max

      // データセットの配列を作成
      const datasets = [{
        label: '気温',
        data: temperatureData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderWidth: 3,
        showLine: true,
        fill: false,
        tension: 0.2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderWidth: 1,
        pointHoverBorderWidth: 2
      }, {
        label: '露点温度',
        data: dewPointData,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderWidth: 3,
        showLine: true,
        fill: false,
        tension: 0.2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderWidth: 1,
        pointHoverBorderWidth: 2
      }]

      // 地表気温データがある場合は追加
      if (surfaceTemp !== null) {
        datasets.push({
          label: '地表気温(2m)',
          data: [{ x: surfaceTemp, y: surfaceElevation }],
          borderColor: 'rgb(220, 38, 38)',
          backgroundColor: 'rgb(220, 38, 38)',
          borderWidth: 0,
          showLine: false,
          fill: false,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 1,
          pointHoverBorderWidth: 2,
          pointStyle: 'triangle'
        })
      }

      // 地表露点温度データがある場合は追加
      if (surfaceDewPoint !== null) {
        datasets.push({
          label: '地表露点温度(2m)',
          data: [{ x: surfaceDewPoint, y: surfaceElevation }],
          borderColor: 'rgb(29, 78, 216)',
          backgroundColor: 'rgb(29, 78, 216)',
          borderWidth: 0,
          showLine: false,
          fill: false,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 1,
          pointHoverBorderWidth: 2,
          pointStyle: 'triangle'
        })
      }

      // 乾燥断熱線を計算して追加（地表気温から上方へ）
      if (surfaceTemp !== null) {
        const dryAdiabaticData = []
        const maxHeight = Math.max(...heights)
        const dryAdiabaticLapseRate = 9.8 // °C/km (乾燥断熱減率)
        
        // 200m間隔で点を生成
        for (let height = surfaceElevation; height <= maxHeight; height += 200) {
          const heightKm = height / 1000
          const surfaceHeightKm = surfaceElevation / 1000
          const temp = surfaceTemp - dryAdiabaticLapseRate * (heightKm - surfaceHeightKm)
          dryAdiabaticData.push({ x: temp, y: height })
        }
        
        datasets.push({
          label: '乾燥断熱線',
          data: dryAdiabaticData,
          borderColor: 'rgba(220, 38, 38, 0.6)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          showLine: true,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0
        })
      }

      // 混合比線を計算して追加（地表露点温度から上方へ）
      if (surfaceDewPoint !== null) {
        const mixingRatioData = []
        
        // 実際の気圧面データから高度と気圧の対応を作成
        const pressureHeightData = pressureLevels.map(level => {
          const levelKey = `${level}hPa`
          return {
            pressure: level,
            height: selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
          }
        }).filter(data => data.height > 0).sort((a, b) => a.height - b.height)
        
        console.log('Pressure-height data:', pressureHeightData)
        
        // 地表での実際の混合比を計算（露点温度から）
        // 海面気圧から地表面（surface_elevation）の気圧を計算
        const pressure_msl = selectedForecast.value.surface?.pressure_msl ||
                             selectedForecast.value.surface?.pressure ||
                             1013.25 // hPa（フォールバック値）
        const elevation = baseElevation // 地表面高度 [m]
        // ISA標準大気モデルで地表気圧を計算
        const surfacePressure = pressure_msl * Math.pow(1 - elevation / 44330.77, 5.25588)

        const es0 = 6.112 * Math.exp((17.67 * surfaceDewPoint) / (surfaceDewPoint + 243.5)) // 地表での飽和蒸気圧（露点温度での）
        const actualMixingRatio = 0.622 * es0 / (surfacePressure - es0) // kg/kg（地表での実際の混合比）
        
        console.log('Surface dew point:', surfaceDewPoint, 'Actual mixing ratio:', actualMixingRatio)
        
        // 高度に対応する気圧を線形補間で求める関数
        const getPressureAtHeight = (targetHeight) => {
          if (pressureHeightData.length === 0) return null
          
          // 範囲外チェック
          if (targetHeight <= pressureHeightData[0].height) return pressureHeightData[0].pressure
          if (targetHeight >= pressureHeightData[pressureHeightData.length - 1].height) {
            return pressureHeightData[pressureHeightData.length - 1].pressure
          }
          
          // 線形補間
          for (let i = 0; i < pressureHeightData.length - 1; i++) {
            if (targetHeight >= pressureHeightData[i].height && targetHeight <= pressureHeightData[i + 1].height) {
              const h1 = pressureHeightData[i].height
              const h2 = pressureHeightData[i + 1].height
              const p1 = pressureHeightData[i].pressure
              const p2 = pressureHeightData[i + 1].pressure
              
              // 高度に対する気圧の線形補間
              const pressure = p1 + (p2 - p1) * (targetHeight - h1) / (h2 - h1)
              return pressure
            }
          }
          return null
        }
        
        // 地表から最高高度まで200m間隔で混合比一定線を計算
        const maxHeight = Math.max(...heights)
        let isFirstLoop = true
        for (let height = surfaceElevation; height <= maxHeight; height += 200) {
          // 1ループ目（地表高度）では実際の地表気圧を使用
          const pressure = isFirstLoop ? surfacePressure : getPressureAtHeight(height)
          isFirstLoop = false

          if (pressure && pressure > 0) {
            // 混合比一定として、この高度での飽和蒸気圧を計算
            const es = actualMixingRatio * pressure / (0.622 + actualMixingRatio)

            // Magnus式の逆関数で露点温度を計算
            if (es > 0.01) { // 最小蒸気圧チェック
              const lnEs = Math.log(es / 6.112)
              const dewPointTemp = (243.5 * lnEs) / (17.67 - lnEs)

              console.log('Height:', height, 'Pressure:', pressure, 'Es:', es, 'Dew point:', dewPointTemp)

              if (!isNaN(dewPointTemp) && isFinite(dewPointTemp) && dewPointTemp > -80 && dewPointTemp < 60) {
                mixingRatioData.push({ x: dewPointTemp, y: height })
              }
            }
          }
        }
        
        if (mixingRatioData.length > 1) {
          datasets.push({
            label: '混合比線',
            data: mixingRatioData,
            borderColor: 'rgba(29, 78, 216, 0.6)',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [3, 3],
            showLine: true,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0
          })
        }
      }

      // テイクオフ高度の横線を追加
      const takeoffElevation = weatherData.value?.takeoff_location?.elevation
      if (takeoffElevation) {
        datasets.push({
          label: 'テイクオフ高度',
          data: [
            { x: xAxisMin, y: takeoffElevation },
            { x: xAxisMax, y: takeoffElevation }
          ],
          borderColor: 'rgb(249, 115, 22)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [10, 5],
          showLine: true,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0
        })
      }

      // ランディング高度の横線を追加
      const landingElevation = weatherData.value?.landing_location?.elevation
      if (landingElevation) {
        datasets.push({
          label: 'ランディング高度',
          data: [
            { x: xAxisMin, y: landingElevation },
            { x: xAxisMax, y: landingElevation }
          ],
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [10, 5],
          showLine: true,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0
        })
      }

      // 境界層高度の横線を追加
      const blh = selectedForecast.value?.surface?.boundary_layer_height
      if (blh != null) {
        datasets.push({
          label: `境界層高度 (${Math.round(blh)}m)`,
          data: [
            { x: xAxisMin, y: blh },
            { x: xAxisMax, y: blh }
          ],
          borderColor: 'rgb(139, 92, 246)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 3],
          showLine: true,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0
        })
      }

      // 雲量背景プラグイン（各気圧面の雲量を高度帯ごとに背景色で表示）
      const cloudBackgroundPlugin = {
        id: 'cloudBackgroundPlugin',
        beforeDatasetsDraw: (chart) => {
          if (!selectedForecast.value?.pressure_levels) return

          const chartCtx = chart.ctx
          const xScale = chart.scales.x
          const yScale = chart.scales.y
          const chartArea = chart.chartArea

          // 現在の時刻の各気圧面データを取得
          const sortedLevels = pressureLevels
            .map(level => {
              const key = `${level}hPa`
              const pl = selectedForecast.value.pressure_levels?.[key]
              return {
                height: pl?.geopotential_height ?? null,
                cloud: pl?.cloud_cover ?? null
              }
            })
            .filter(d => d.height != null && d.height > 0)
            .sort((a, b) => a.height - b.height)

          if (sortedLevels.length === 0) return

          function cloudBgColor(pct) {
            if (pct == null || isNaN(pct)) return '#D6EAF8'
            const t = Math.min(Math.max(pct / 100, 0), 1)
            if (t < 0.05) return '#D6EAF8'
            if (t < 0.15) {
              const r = (t - 0.05) / 0.10
              return `rgb(${lerp(214,224,r)},${lerp(234,224,r)},${lerp(248,224,r)})`
            }
            if (t < 0.50) {
              const r = (t - 0.15) / 0.35
              return `rgb(${lerp(224,144,r)},${lerp(224,144,r)},${lerp(224,144,r)})`
            }
            const r = (t - 0.50) / 0.50
            return `rgb(${lerp(144,48,r)},${lerp(144,48,r)},${lerp(144,48,r)})`
          }
          function lerp(a, b, t) { return Math.round(a + (b - a) * t) }

          chartCtx.save()
          for (let i = 0; i < sortedLevels.length; i++) {
            const { height, cloud } = sortedLevels[i]

            // 隣接気圧面の中点を上下境界にする
            let yBottom, yTop
            if (i === 0) {
              const gap = sortedLevels.length > 1 ? sortedLevels[1].height - height : 200
              yBottom = yScale.getPixelForValue(height - gap / 2)
            } else {
              yBottom = yScale.getPixelForValue((height + sortedLevels[i - 1].height) / 2)
            }
            if (i === sortedLevels.length - 1) {
              const gap = sortedLevels.length > 1 ? height - sortedLevels[i - 1].height : 200
              yTop = yScale.getPixelForValue(height + gap / 2)
            } else {
              yTop = yScale.getPixelForValue((height + sortedLevels[i + 1].height) / 2)
            }

            const top = Math.max(Math.min(yTop, yBottom), chartArea.top)
            const bottom = Math.min(Math.max(yTop, yBottom), chartArea.bottom)

            chartCtx.fillStyle = cloudBgColor(cloud)
            chartCtx.globalAlpha = 0.6
            chartCtx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, bottom - top)
          }
          chartCtx.globalAlpha = 1.0
          chartCtx.restore()
        }
      }

      // 上昇気流領域を塗りつぶすカスタムプラグイン
      // アニメーション対応: グラフのアニメーション完了まで待ってから表示
      const thermalAreaPlugin = {
        id: 'thermalAreaPlugin',
        beforeDatasetsDraw: (chart) => {
          // データセットの前に描画（線の下に塗りつぶし）
          const ctx = chart.ctx
          const xScale = chart.scales.x
          const yScale = chart.scales.y

          // 気温線と乾燥断熱線のデータを取得
          const tempDataset = chart.data.datasets.find(ds => ds.label === '気温')
          const dryAdiabaticDataset = chart.data.datasets.find(ds => ds.label === '乾燥断熱線')

          if (!tempDataset || !dryAdiabaticDataset) return

          const tempData = [...tempDataset.data].sort((a, b) => a.y - b.y)
          const dryData = [...dryAdiabaticDataset.data].sort((a, b) => a.y - b.y)

          if (tempData.length === 0 || dryData.length === 0) return

          // 高度の範囲を取得
          const minHeight = Math.max(tempData[0].y, dryData[0].y)
          const maxHeight = Math.min(tempData[tempData.length - 1].y, dryData[dryData.length - 1].y)

          // 高度を細かく分割して、各高度での温度を補間
          const step = 50 // 50m間隔
          const points = []

          for (let height = minHeight; height <= maxHeight; height += step) {
            // 気温線での温度を線形補間
            let tempAtHeight = null
            for (let i = 0; i < tempData.length - 1; i++) {
              if (tempData[i].y <= height && height <= tempData[i + 1].y) {
                const ratio = (height - tempData[i].y) / (tempData[i + 1].y - tempData[i].y)
                tempAtHeight = tempData[i].x + ratio * (tempData[i + 1].x - tempData[i].x)
                break
              }
            }

            // 乾燥断熱線での温度を線形補間
            let dryTempAtHeight = null
            for (let i = 0; i < dryData.length - 1; i++) {
              if (dryData[i].y <= height && height <= dryData[i + 1].y) {
                const ratio = (height - dryData[i].y) / (dryData[i + 1].y - dryData[i].y)
                dryTempAtHeight = dryData[i].x + ratio * (dryData[i + 1].x - dryData[i].x)
                break
              }
            }

            if (tempAtHeight !== null && dryTempAtHeight !== null) {
              points.push({
                height,
                temp: tempAtHeight,
                dryTemp: dryTempAtHeight,
                isThermal: dryTempAtHeight > tempAtHeight
              })
            }
          }

          // 上昇気流領域を塗りつぶす
          ctx.save()
          ctx.fillStyle = 'rgba(255, 193, 7, 0.25)'  // 黄色の半透明

          // 連続する上昇気流領域ごとにパスを作成
          let currentPath = []

          for (let i = 0; i < points.length; i++) {
            if (points[i].isThermal) {
              currentPath.push(points[i])
            } else {
              // 上昇気流領域が終わった場合、パスを描画
              if (currentPath.length > 0) {
                ctx.beginPath()

                // 下から上へ気温線に沿って描画
                for (let j = 0; j < currentPath.length; j++) {
                  const px = xScale.getPixelForValue(currentPath[j].temp)
                  const py = yScale.getPixelForValue(currentPath[j].height)
                  if (j === 0) {
                    ctx.moveTo(px, py)
                  } else {
                    ctx.lineTo(px, py)
                  }
                }

                // 上から下へ乾燥断熱線に沿って描画
                for (let j = currentPath.length - 1; j >= 0; j--) {
                  const px = xScale.getPixelForValue(currentPath[j].dryTemp)
                  const py = yScale.getPixelForValue(currentPath[j].height)
                  ctx.lineTo(px, py)
                }

                ctx.closePath()
                ctx.fill()
                currentPath = []
              }
            }
          }

          // 最後の領域を描画
          if (currentPath.length > 0) {
            ctx.beginPath()

            for (let j = 0; j < currentPath.length; j++) {
              const px = xScale.getPixelForValue(currentPath[j].temp)
              const py = yScale.getPixelForValue(currentPath[j].height)
              if (j === 0) {
                ctx.moveTo(px, py)
              } else {
                ctx.lineTo(px, py)
              }
            }

            for (let j = currentPath.length - 1; j >= 0; j--) {
              const px = xScale.getPixelForValue(currentPath[j].dryTemp)
              const py = yScale.getPixelForValue(currentPath[j].height)
              ctx.lineTo(px, py)
            }

            ctx.closePath()
            ctx.fill()
          }

          ctx.restore()
        }
      }

      emagramChartInstance = new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: datasets
        },
        plugins: [cloudBackgroundPlugin, thermalAreaPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: { display: true, text: '気温 (℃)', font: { size: 14, weight: 'bold' } },
              min: xAxisMin,
              max: xAxisMax,
              grid: {
                color: 'rgba(0, 0, 0, 0.1)',
                lineWidth: 1
              },
              ticks: {
                stepSize: 5
              }
            },
            y: {
              title: { display: true, text: '高度 (m)', font: { size: 14, weight: 'bold' } },
              min: 0,
              max: Math.ceil(Math.max(...heights) / 200) * 200,
              grid: {
                color: 'rgba(0, 0, 0, 0.1)',
                lineWidth: 1
              },
              ticks: {
                stepSize: 200,
                callback: function(value) {
                  return value + 'm'
                }
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                font: { size: 12, weight: 'bold' }
              }
            },
            tooltip: {
              callbacks: {
                title: function(context) {
                  const point = context[0]
                  return `高度: ${point.parsed.y}m`
                },
                label: function(context) {
                  return `${context.dataset.label}: ${context.parsed.x.toFixed(1)}℃`
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            intersect: true
          }
        }
      })
    }

    const updateEmagramChartData = () => {
      if (!emagramChartInstance || !selectedForecast.value) return

      const heights = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
      })

      const temperatures = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.temperature || 0
      })

      const dewPoints = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        return selectedForecast.value.pressure_levels?.[levelKey]?.dew_point || 0
      })

      // データポイントを更新
      const temperatureData = temperatures.map((temp, index) => ({
        x: temp,
        y: heights[index]
      }))

      const dewPointData = dewPoints.map((dewPoint, index) => ({
        x: dewPoint,
        y: heights[index]
      }))

      // 地表データ（surface_elevation + 2m高度）
      const surfaceTemp = selectedForecast.value.surface?.temperature || null
      const surfaceDewPoint = selectedForecast.value.surface?.dew_point || null
      // 基準高度の扱いは初期描画側と同じ（surface_elevation 優先）
      const baseElevation = selectedForecast.value.surface_elevation ??
                            (selectedForecast.value.model_elevation || 0)
      const surfaceElevation = baseElevation + 2 // 地表面 + 2m

      // 同じ日の全時刻データからX軸範囲を計算（日付が変わらない限り固定）
      const xAxisRange = calculateEmagramXAxisRange()
      const xAxisMin = xAxisRange.min
      const xAxisMax = xAxisRange.max

      // X軸の範囲を更新
      emagramChartInstance.options.scales.x.min = xAxisMin
      emagramChartInstance.options.scales.x.max = xAxisMax

      // チャートデータを更新（アニメーションあり）
      const datasets = emagramChartInstance.data.datasets

      // 気温データセットを更新
      if (datasets[0]) {
        datasets[0].data = temperatureData
      }

      // 露点温度データセットを更新
      if (datasets[1]) {
        datasets[1].data = dewPointData
      }

      // 地表気温データセットを更新
      let datasetIndex = 2
      if (surfaceTemp !== null && datasets[datasetIndex]) {
        datasets[datasetIndex].data = [{ x: surfaceTemp, y: surfaceElevation }]
        datasetIndex++
      }

      // 地表露点温度データセットを更新
      if (surfaceDewPoint !== null && datasets[datasetIndex]) {
        datasets[datasetIndex].data = [{ x: surfaceDewPoint, y: surfaceElevation }]
        datasetIndex++
      }

      // 乾燥断熱線データセットを更新
      if (surfaceTemp !== null && datasets[datasetIndex]) {
        const dryAdiabaticData = []
        const maxHeight = Math.max(...heights)
        const dryAdiabaticLapseRate = 9.8 // °C/km (乾燥断熱減率)
        
        // 200m間隔で点を生成
        for (let height = surfaceElevation; height <= maxHeight; height += 200) {
          const heightKm = height / 1000
          const surfaceHeightKm = surfaceElevation / 1000
          const temp = surfaceTemp - dryAdiabaticLapseRate * (heightKm - surfaceHeightKm)
          dryAdiabaticData.push({ x: temp, y: height })
        }
        
        datasets[datasetIndex].data = dryAdiabaticData
        datasetIndex++
      }

      // 混合比線データセットを更新
      if (surfaceDewPoint !== null && datasets[datasetIndex]) {
        const mixingRatioData = []
        
        // 実際の気圧面データから高度と気圧の対応を作成
        const pressureHeightData = pressureLevels.map(level => {
          const levelKey = `${level}hPa`
          return {
            pressure: level,
            height: selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
          }
        }).filter(data => data.height > 0).sort((a, b) => a.height - b.height)
        
        // 地表での実際の混合比を計算（露点温度から）
        // 海面気圧から地表面（surface_elevation）の気圧を計算
        const pressure_msl = selectedForecast.value.surface?.pressure_msl ||
                             selectedForecast.value.surface?.pressure ||
                             1013.25 // hPa（フォールバック値）
        const elevation = baseElevation // 地表面高度 [m]
        // ISA標準大気モデルで地表気圧を計算
        const surfacePressure = pressure_msl * Math.pow(1 - elevation / 44330.77, 5.25588)

        const es0 = 6.112 * Math.exp((17.67 * surfaceDewPoint) / (surfaceDewPoint + 243.5))
        const actualMixingRatio = 0.622 * es0 / (surfacePressure - es0)
        
        // 高度に対応する気圧を線形補間で求める関数
        const getPressureAtHeight = (targetHeight) => {
          if (pressureHeightData.length === 0) return null
          
          if (targetHeight <= pressureHeightData[0].height) return pressureHeightData[0].pressure
          if (targetHeight >= pressureHeightData[pressureHeightData.length - 1].height) {
            return pressureHeightData[pressureHeightData.length - 1].pressure
          }
          
          for (let i = 0; i < pressureHeightData.length - 1; i++) {
            if (targetHeight >= pressureHeightData[i].height && targetHeight <= pressureHeightData[i + 1].height) {
              const h1 = pressureHeightData[i].height
              const h2 = pressureHeightData[i + 1].height
              const p1 = pressureHeightData[i].pressure
              const p2 = pressureHeightData[i + 1].pressure
              
              const pressure = p1 + (p2 - p1) * (targetHeight - h1) / (h2 - h1)
              return pressure
            }
          }
          return null
        }
        
        // 地表から最高高度まで200m間隔で混合比一定線を計算
        const maxHeight = Math.max(...heights)
        let isFirstLoop = true
        for (let height = surfaceElevation; height <= maxHeight; height += 200) {
          // 1ループ目（地表高度）では実際の地表気圧を使用
          const pressure = isFirstLoop ? surfacePressure : getPressureAtHeight(height)
          isFirstLoop = false

          if (pressure && pressure > 0) {
            const es = actualMixingRatio * pressure / (0.622 + actualMixingRatio)

            // Magnus式の逆関数で露点温度を計算
            if (es > 0.01) { // 最小蒸気圧チェック
              const lnEs = Math.log(es / 6.112)
              const dewPoint = (243.5 * lnEs) / (17.67 - lnEs)

              if (!isNaN(dewPoint) && isFinite(dewPoint) && dewPoint > -80 && dewPoint < 60) {
                mixingRatioData.push({ x: dewPoint, y: height })
              }
            }
          }
        }
        
        datasets[datasetIndex].data = mixingRatioData
        datasetIndex++
      }

      // テイクオフ高度の横線を更新（オレンジ）
      const takeoffElevation = weatherData.value?.takeoff_location?.elevation
      if (takeoffElevation && datasets[datasetIndex]) {
        datasets[datasetIndex].data = [
          { x: xAxisMin, y: takeoffElevation },
          { x: xAxisMax, y: takeoffElevation }
        ]
        datasetIndex++
      }

      // ランディング高度の横線を更新（緑）
      const landingElevation = weatherData.value?.landing_location?.elevation
      if (landingElevation && datasets[datasetIndex]) {
        datasets[datasetIndex].data = [
          { x: xAxisMin, y: landingElevation },
          { x: xAxisMax, y: landingElevation }
        ]
        datasetIndex++
      }

      // 境界層高度の横線を更新（紫）
      const blh = selectedForecast.value?.surface?.boundary_layer_height
      if (datasets[datasetIndex]) {
        if (blh != null) {
          datasets[datasetIndex].data = [
            { x: xAxisMin, y: blh },
            { x: xAxisMax, y: blh }
          ]
          datasets[datasetIndex].label = `境界層高度 (${Math.round(blh)}m)`
        } else {
          datasets[datasetIndex].data = []
        }
      }

      // チャートを更新
      emagramChartInstance.update()
    }

    const drawCloudColormap = () => {
      const canvas = cloudColormapCanvas.value
      if (!canvas || !dailyForecast.value.length) return

      const forecasts = dailyForecast.value
      const samplePL = forecasts[0]?.pressure_levels || {}
      const pressureLevelsAvail = Object.keys(samplePL)
        .map(k => parseInt(k))
        .filter(n => !isNaN(n))
        .sort((a, b) => b - a) // 高気圧(低高度)が先

      if (pressureLevelsAvail.length === 0) return

      // 各気圧面の平均ジオポテンシャル高度を計算（全時刻平均）
      const levelData = pressureLevelsAvail.map(level => {
        let hSum = 0, hCount = 0
        for (const f of forecasts) {
          const pl = f.pressure_levels?.[`${level}hPa`]
          const gh = pl?.geopotential_height ?? pl?.height
          if (gh != null && !isNaN(gh)) { hSum += gh; hCount++ }
        }
        return { level, height: hCount > 0 ? hSum / hCount : null }
      }).filter(d => d.height != null)

      if (levelData.length === 0) return

      // 高度昇順にソート（低い高度=index 0）
      levelData.sort((a, b) => a.height - b.height)

      const nTimes = forecasts.length
      const nLevels = levelData.length
      const minH = levelData[0].height
      const maxH = levelData[nLevels - 1].height

      // 各データポイントの時刻（時間）を取得し、時刻ベースでX座標を計算
      const timeHours = forecasts.map(f => {
        const h = parseInt(f.time.slice(11, 13), 10)
        const m = parseInt(f.time.slice(14, 16), 10)
        return h + m / 60
      })
      // 1日の時間範囲（0〜24）
      const dayStartH = timeHours[0]
      const dayEndH = timeHours[nTimes - 1] < dayStartH
        ? timeHours[nTimes - 1] + 24
        : timeHours[nTimes - 1]
      // 最後のデータ点のセル幅を推定（直前の間隔を使用、なければ1h）
      const lastGap = nTimes >= 2
        ? timeHours[nTimes - 1] - timeHours[nTimes - 2]
        : 1
      const totalHours = dayEndH - dayStartH + lastGap

      // キャンバスサイズ
      const dpr = window.devicePixelRatio || 1
      const labelWidth = 60
      const colorbarWidth = 50
      const topPad = 10
      const bottomPad = 40
      const plotWidth = canvas.parentElement.clientWidth - labelWidth - colorbarWidth
      const plotH = 300

      // 時刻→X座標変換
      function timeToX(h) {
        return labelWidth + (h - dayStartH) / totalHours * plotWidth
      }

      canvas.width = (labelWidth + plotWidth + colorbarWidth) * dpr
      canvas.height = (topPad + plotH + bottomPad) * dpr
      canvas.style.width = `${labelWidth + plotWidth + colorbarWidth}px`
      canvas.style.height = `${topPad + plotH + bottomPad}px`

      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)

      function cloudColor(pct) {
        if (pct == null || isNaN(pct)) return '#D6EAF8'
        const t = Math.min(Math.max(pct / 100, 0), 1)
        if (t < 0.05) return '#D6EAF8'
        if (t < 0.15) return lerpColor('#D6EAF8', '#E0E0E0', (t - 0.05) / 0.10)
        if (t < 0.50) return lerpColor('#E0E0E0', '#909090', (t - 0.15) / 0.35)
        return lerpColor('#909090', '#303030', (t - 0.50) / 0.50)
      }

      function lerpColor(c1, c2, t) {
        const r1 = parseInt(c1.slice(1,3),16), g1 = parseInt(c1.slice(3,5),16), b1 = parseInt(c1.slice(5,7),16)
        const r2 = parseInt(c2.slice(1,3),16), g2 = parseInt(c2.slice(3,5),16), b2 = parseInt(c2.slice(5,7),16)
        const r = Math.round(r1+(r2-r1)*t), g = Math.round(g1+(g2-g1)*t), b = Math.round(b1+(b2-b1)*t)
        return `rgb(${r},${g},${b})`
      }

      // 高度→Y座標変換（下=低高度、上=高高度）
      function heightToY(h) {
        return topPad + plotH - (h - minH) / (maxH - minH) * plotH
      }

      // 背景クリア
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr)

      // セル描画（高度に比例した位置に配置、時刻ベースのX座標）
      for (let li = 0; li < nLevels; li++) {
        const { level, height: h } = levelData[li]
        const hNext = li < nLevels - 1 ? levelData[li + 1].height : h + (h - levelData[li - 1].height)
        const yTop = Math.floor(heightToY((h + hNext) / 2))
        const hPrev = li > 0 ? levelData[li - 1].height : h - (levelData[li + 1].height - h)
        const yBot = Math.ceil(heightToY((h + hPrev) / 2))
        const cellHeight = yBot - yTop + 1

        for (let ti = 0; ti < nTimes; ti++) {
          const pl = forecasts[ti]?.pressure_levels?.[`${level}hPa`]
          const xLeft = Math.floor(timeToX(timeHours[ti]))
          // セルの右端 = 次のデータ点の開始位置、最後のセルは推定
          const nextH = ti < nTimes - 1 ? timeHours[ti + 1] : timeHours[ti] + lastGap
          const xRight = Math.ceil(timeToX(nextH))
          ctx.fillStyle = cloudColor(pl?.cloud_cover)
          ctx.fillRect(xLeft, yTop, xRight - xLeft + 1, cellHeight)
        }
      }

      // グリッド線（500m刻み・3時間刻み）
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)'
      ctx.lineWidth = 1

      // 水平グリッド（500m刻み）
      const tickInterval = 500
      const firstTick = Math.ceil(minH / tickInterval) * tickInterval
      const plotRight = labelWidth + plotWidth
      for (let h = firstTick; h <= maxH; h += tickInterval) {
        const y = heightToY(h)
        if (y < topPad || y > topPad + plotH) continue
        ctx.beginPath()
        ctx.moveTo(labelWidth, y)
        ctx.lineTo(plotRight, y)
        ctx.stroke()
      }

      // 垂直グリッド（3時間刻み）+ X軸ラベル（時刻ベース）
      ctx.fillStyle = '#374151'
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      const gridStartH = Math.ceil(dayStartH / 3) * 3
      for (let gh = gridStartH; gh <= dayEndH + lastGap; gh += 3) {
        const x = timeToX(gh)
        if (x < labelWidth || x > plotRight) continue
        // グリッド線
        if (gh > dayStartH) {
          ctx.beginPath()
          ctx.moveTo(x, topPad)
          ctx.lineTo(x, topPad + plotH)
          ctx.stroke()
        }
        // ラベル
        const displayH = gh % 24
        ctx.fillText(`${displayH}:00`, x, topPad + plotH + 10)
      }
      // 右端のグリッド線とラベル（24:00）
      {
        const xEnd = timeToX(dayEndH + lastGap)
        ctx.beginPath()
        ctx.moveTo(xEnd, topPad)
        ctx.lineTo(xEnd, topPad + plotH)
        ctx.stroke()
        const endDisplayH = Math.round(dayEndH + lastGap) % 24
        if (endDisplayH !== Math.round(gridStartH) % 24) {
          ctx.fillText(endDisplayH === 0 ? '24:00' : `${endDisplayH}:00`, xEnd, topPad + plotH + 10)
        }
      }

      // Y軸ラベル（500m刻み）
      ctx.fillStyle = '#374151'
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'middle'

      for (let h = firstTick; h <= maxH; h += tickInterval) {
        const y = heightToY(h)
        if (y < topPad || y > topPad + plotH) continue
        ctx.fillText(`${h}m`, labelWidth - 4, y)
      }

      // 選択時刻のハイライト線（時刻ベース、黄色破線）
      if (selectedTimeIndex.value >= 0 && selectedTimeIndex.value < nTimes) {
        const hlX = timeToX(timeHours[selectedTimeIndex.value])
        ctx.save()
        ctx.strokeStyle = 'rgba(234, 179, 8, 0.8)'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.moveTo(hlX, topPad)
        ctx.lineTo(hlX, topPad + plotH)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.restore()
      }

      // カラーバー
      const barX = plotRight + 10
      const barW = 12
      for (let i = 0; i < plotH; i++) {
        ctx.fillStyle = cloudColor((1 - i / plotH) * 100)
        ctx.fillRect(barX, topPad + i, barW, 1)
      }
      ctx.fillStyle = '#374151'
      ctx.font = '11px sans-serif'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      for (const pct of [0, 25, 50, 75, 100]) {
        ctx.fillText(`${pct}%`, barX + barW + 3, topPad + plotH * (1 - pct / 100))
      }
    }

    const createHodographChart = () => {
      if (!selectedForecast.value) return

      const ctx = hodographChart.value?.getContext('2d')
      if (!ctx) return

      // 既存のチャートがある場合は、データのみ更新
      if (hodographChartInstance) {
        updateHodographChartData()
        return
      }

      // 高度別の色設定
      const altitudeColors = [
        { color: 'rgb(139, 69, 19)', bg: 'rgba(139, 69, 19, 0.6)' },   // 1000hPa - 茶色（地表）
        { color: 'rgb(255, 0, 0)', bg: 'rgba(255, 0, 0, 0.6)' },       // 975hPa - 赤
        { color: 'rgb(255, 165, 0)', bg: 'rgba(255, 165, 0, 0.6)' },   // 950hPa - オレンジ
        { color: 'rgb(255, 255, 0)', bg: 'rgba(255, 255, 0, 0.6)' },   // 925hPa - 黄色
        { color: 'rgb(0, 255, 0)', bg: 'rgba(0, 255, 0, 0.6)' },       // 900hPa - 緑
        { color: 'rgb(0, 0, 255)', bg: 'rgba(0, 0, 255, 0.6)' },       // 850hPa - 青
        { color: 'rgb(75, 0, 130)', bg: 'rgba(75, 0, 130, 0.6)' },     // 800hPa - インディゴ
        { color: 'rgb(128, 0, 128)', bg: 'rgba(128, 0, 128, 0.6)' },   // 750hPa - 紫
        { color: 'rgb(0, 0, 0)', bg: 'rgba(0, 0, 0, 0.6)' }            // 700hPa - 黒（高層）
      ]

      // 凡例の並び順を逆にするため、逆順で処理
      const datasets = [...pressureLevels].reverse().map((level, index) => {
        const levelKey = `${level}hPa`
        const windSpeed = selectedForecast.value.pressure_levels?.[levelKey]?.wind_speed || 0
        const windDirection = selectedForecast.value.pressure_levels?.[levelKey]?.wind_direction || 0
        const height = selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
        
        // 風向から風ベクトルのx, y成分を計算
        // 気象学的風向：風が吹いてくる方向なので、ベクトルは180度反対方向
        const radians = ((windDirection + 180) * Math.PI) / 180
        const x = windSpeed * Math.sin(radians)  // 東西成分（東が正）
        const y = windSpeed * Math.cos(radians)  // 南北成分（北が正）
        
        // 元の配列での色インデックスを取得（逆順なので調整）
        const colorIndex = pressureLevels.length - 1 - index
        
        return {
          label: `${height}m`,
          data: [{ x, y, level, windSpeed, windDirection, height }],
          borderColor: altitudeColors[colorIndex].color,
          backgroundColor: altitudeColors[colorIndex].bg,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 1,
          pointHoverBorderWidth: 2,
          showLine: false
        }
      })

      // 線でつなぐデータセットを追加
      const windData = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        const windSpeed = selectedForecast.value.pressure_levels?.[levelKey]?.wind_speed || 0
        const windDirection = selectedForecast.value.pressure_levels?.[levelKey]?.wind_direction || 0
        
        const radians = ((windDirection + 180) * Math.PI) / 180
        const x = windSpeed * Math.sin(radians)
        const y = windSpeed * Math.cos(radians)
        
        return { x, y }
      })

      datasets.push({
        label: '風ベクトル線',
        data: windData,
        borderColor: 'rgba(128, 128, 128, 0.5)',
        backgroundColor: 'transparent',
        showLine: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        borderWidth: 2,
        borderDash: [5, 5]
      })

      // 同心円をデータセットとして追加
      const radii = [2.5, 5, 7.5, 10, 15]
      radii.forEach(radius => {
        // 円を100分割して座標点を生成
        const circleData = []
        for (let i = 0; i <= 100; i++) {
          const angle = (i / 100) * 2 * Math.PI
          const x = radius * Math.cos(angle)
          const y = radius * Math.sin(angle)
          circleData.push({ x, y })
        }
        
        datasets.push({
          label: `${radius}m/s`,
          data: circleData,
          borderColor: radius === 5 ? '#000000' : 'rgba(100, 100, 100, 0.6)', // 5m/sのみ完全な黒に変更
          backgroundColor: 'transparent',
          showLine: true,
          pointRadius: 0, // マーカーを非表示
          pointHoverRadius: 0, // ホバー時のマーカーも非表示
          tension: 0,
          borderWidth: 1,
          borderDash: [2, 2]
        })
      })

      // 矢印と同心円ラベルを描画するプラグイン
      const arrowAndLabelsPlugin = {
        id: 'arrowAndLabelsPlugin',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx

          // 1000hPaのデータを見つける（矢印描画）
          // chart.data.datasetsから探し、data[0].levelプロパティをチェック
          const datasetIndex = chart.data.datasets.findIndex(dataset =>
            dataset.data && dataset.data.length > 0 && dataset.data[0].level === 1000
          )

          if (datasetIndex !== -1) {
            const surface1000hPaDataset = chart.data.datasets[datasetIndex]
            const point = surface1000hPaDataset.data[0]
            const meta = chart.getDatasetMeta(datasetIndex)
            
            if (meta.data.length > 0) {
              const canvasPos = meta.data[0]
              const originX = chart.scales.x.getPixelForValue(0)
              const originY = chart.scales.y.getPixelForValue(0)
              
              // 矢印を描画
              ctx.save()
              ctx.strokeStyle = 'rgba(139, 69, 19, 0.8)'  // 1000hPaと同じ茶色
              ctx.lineWidth = 3
              
              // 矢印の線を描画
              ctx.beginPath()
              ctx.moveTo(originX, originY)
              ctx.lineTo(canvasPos.x, canvasPos.y)
              ctx.stroke()
              
              // 矢じりを描画
              const angle = Math.atan2(canvasPos.y - originY, canvasPos.x - originX)
              const arrowLength = 12
              const arrowAngle = Math.PI / 6
              
              ctx.beginPath()
              ctx.moveTo(canvasPos.x, canvasPos.y)
              ctx.lineTo(
                canvasPos.x - arrowLength * Math.cos(angle - arrowAngle),
                canvasPos.y - arrowLength * Math.sin(angle - arrowAngle)
              )
              ctx.moveTo(canvasPos.x, canvasPos.y)
              ctx.lineTo(
                canvasPos.x - arrowLength * Math.cos(angle + arrowAngle),
                canvasPos.y - arrowLength * Math.sin(angle + arrowAngle)
              )
              ctx.stroke()
              
              ctx.restore()
            }
          }
          
          // 同心円の半径ラベルを描画
          const originX = chart.scales.x.getPixelForValue(0)
          const originY = chart.scales.y.getPixelForValue(0)
          
          ctx.save()
          ctx.font = '12px Arial'
          ctx.fillStyle = 'rgba(60, 60, 60, 0.8)'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          
          const radii = [2.5, 5, 7.5, 10, 15]
          radii.forEach(radius => {
            // 右上（45度方向）にラベルを表示
            const labelX = chart.scales.x.getPixelForValue(radius * 0.707) // cos(45°) ≈ 0.707
            const labelY = chart.scales.y.getPixelForValue(radius * 0.707) // sin(45°) ≈ 0.707
            
            // 背景の白い円を描画（視認性向上）
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
            ctx.beginPath()
            ctx.arc(labelX, labelY, 10, 0, 2 * Math.PI)
            ctx.fill()
            
            // ラベルテキストを描画
            ctx.fillStyle = 'rgba(60, 60, 60, 0.9)'
            ctx.fillText(radius.toString(), labelX, labelY)
          })
          
          ctx.restore()
        }
      }

      hodographChartInstance = new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: datasets
        },
        plugins: [arrowAndLabelsPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: { display: true, text: '東西成分 (m/s)' },
              min: -15,
              max: 15,
              ticks: {
                stepSize: 2.5
              },
              grid: {
                color: function(context) {
                  // X=0の線を太くする
                  return context.tick.value === 0 ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.1)'
                },
                lineWidth: function(context) {
                  return context.tick.value === 0 ? 2 : 1
                }
              }
            },
            y: {
              title: { display: true, text: '南北成分 (m/s)' },
              min: -15,
              max: 15,
              ticks: {
                stepSize: 2.5
              },
              grid: {
                color: function(context) {
                  // Y=0の線を太くする
                  return context.tick.value === 0 ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.1)'
                },
                lineWidth: function(context) {
                  return context.tick.value === 0 ? 2 : 1
                }
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'right',
              labels: {
                usePointStyle: true,
                font: { size: 11 },
                filter: function(legendItem) {
                  // 風ベクトル線と同心円は凡例に表示しない
                  return legendItem.text !== '風ベクトル線' && !legendItem.text.includes('m/s')
                }
              }
            },
            tooltip: {
              callbacks: {
                title: function(context) {
                  const point = context[0].raw
                  if (point && point.level) {
                    return `${point.level}hPa (${point.height}m)`
                  }
                  return 'ホドグラフ'
                },
                label: function(context) {
                  const point = context.raw
                  if (point && point.windSpeed !== undefined) {
                    return [
                      `風速: ${point.windSpeed.toFixed(1)}m/s`,
                      `風向: ${point.windDirection}°`,
                      `東西成分: ${point.x.toFixed(1)}m/s`,
                      `南北成分: ${point.y.toFixed(1)}m/s`
                    ]
                  }
                  return context.dataset.label
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            intersect: true
          }
        }
      })
    }

    const updateHodographChartData = () => {
      if (!hodographChartInstance || !selectedForecast.value) return

      // 凡例の並び順を逆にするため、逆順で処理
      const newDatasets = [...pressureLevels].reverse().map((level, index) => {
        const levelKey = `${level}hPa`
        const windSpeed = selectedForecast.value.pressure_levels?.[levelKey]?.wind_speed || 0
        const windDirection = selectedForecast.value.pressure_levels?.[levelKey]?.wind_direction || 0
        const height = selectedForecast.value.pressure_levels?.[levelKey]?.geopotential_height || 0
        
        // 風向から風ベクトルのx, y成分を計算
        const radians = ((windDirection + 180) * Math.PI) / 180
        const x = windSpeed * Math.sin(radians)
        const y = windSpeed * Math.cos(radians)
        
        return { x, y, level, windSpeed, windDirection, height }
      })

      // 風ベクトル線データを更新
      const windData = pressureLevels.map(level => {
        const levelKey = `${level}hPa`
        const windSpeed = selectedForecast.value.pressure_levels?.[levelKey]?.wind_speed || 0
        const windDirection = selectedForecast.value.pressure_levels?.[levelKey]?.wind_direction || 0
        
        const radians = ((windDirection + 180) * Math.PI) / 180
        const x = windSpeed * Math.sin(radians)
        const y = windSpeed * Math.cos(radians)
        
        return { x, y }
      })

      // 既存のデータセットを更新
      const datasets = hodographChartInstance.data.datasets

      // 各気圧レベルのデータセットを更新
      newDatasets.forEach((newData, index) => {
        if (datasets[index]) {
          datasets[index].data = [newData]
        }
      })

      // 風ベクトル線データセットを更新（通常、気圧レベル数の次のインデックス）
      const windLineDatasetIndex = pressureLevels.length
      if (datasets[windLineDatasetIndex]) {
        datasets[windLineDatasetIndex].data = windData
      }

      // チャートを更新
      hodographChartInstance.update()
    }

    const updateAltitudeCharts = async () => {
      await nextTick()

      // チャートが既に存在する場合はデータのみ更新（滑らかなアニメーション）
      // 存在しない場合は新規作成
      createEmagramChart()
      createHodographChart()
    }

    // Watch for chart tab changes
    watch(activeChartTab, () => {
      createTimeSeriesChart()
    })

    // Watch for selected time index changes to update time series chart + colormap
    watch(selectedTimeIndex, () => {
      createTimeSeriesChart()
      drawCloudColormap()
    })

    // Watch for daily forecast changes to ensure chart updates
    watch(dailyForecast, (newData) => {
      if (newData && newData.length > 0) {
        nextTick(() => {
          setTimeout(() => {
            createTimeSeriesChart()
            updateAltitudeCharts()
          }, 50)
        })
      }
    })

    // Watch for selected forecast changes to update altitude charts
    watch(selectedForecast, (newForecast) => {
      if (newForecast) {
        nextTick(() => {
          setTimeout(() => {
            updateAltitudeCharts()
          }, 50)
        })
      }
    })

    const initializeMap = () => {
      if (!mapContainer.value || !weatherData.value) return

      // テイクオフとランディングの座標を取得
      const takeoffLocation = weatherData.value.takeoff_location
      const landingLocation = weatherData.value.landing_location

      if (!takeoffLocation || !landingLocation) return

      // 既存のマップインスタンスがあれば破棄
      if (mapInstance) {
        mapInstance.remove()
        mapInstance = null
      }

      // 地図の中心を2点の中央に設定
      const centerLat = (takeoffLocation.latitude + landingLocation.latitude) / 2
      const centerLng = (takeoffLocation.longitude + landingLocation.longitude) / 2

      // 地図を初期化（細かいズーム設定）
      mapInstance = L.map(mapContainer.value, {
        zoomDelta: 0.5,           // ＋/-ボタンでのズーム変化量を0.5に（デフォルト1）
        zoomSnap: 0.5,            // ズームレベルを0.5刻みに（デフォルト1）
        wheelPxPerZoomLevel: 120  // スクロールでのズーム感度を下げる（デフォルト60、大きいほど変化が小さい）
      }).setView([centerLat, centerLng], 13)

      // OpenStreetMapタイル（商用利用無料）を追加
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18
      }).addTo(mapInstance)

      // テイクオフ地点のマーカー
      const takeoffIcon = L.divIcon({
        className: 'custom-takeoff-marker',
        html: `
          <div style="
            background: linear-gradient(135deg, #ef4444, #dc2626);
            border: 3px solid white;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            font-size: 18px;
            color: white;
            font-weight: bold;
          ">🪂</div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
      })

      const takeoffMarker = L.marker([takeoffLocation.latitude, takeoffLocation.longitude], { icon: takeoffIcon }).addTo(mapInstance)

      takeoffMarker.bindPopup(`
        <div style="min-width: 200px; font-family: system-ui, sans-serif;">
          <h3 style="margin: 0 0 10px 0; color: #dc2626; font-size: 16px; font-weight: 600;">
            テイクオフ
          </h3>
          <div style="font-size: 14px; color: #6b7280; line-height: 1.5;">
            <div><strong>緯度:</strong> ${takeoffLocation.latitude}°</div>
            <div><strong>経度:</strong> ${takeoffLocation.longitude}°</div>
            <div><strong>標高:</strong> ${takeoffLocation.elevation}m</div>
          </div>
        </div>
      `)

      // ランディング地点のマーカー
      const landingIcon = L.divIcon({
        className: 'custom-landing-marker',
        html: `
          <div style="
            background: linear-gradient(135deg, #10b981, #059669);
            border: 3px solid white;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            font-size: 18px;
            color: white;
            font-weight: bold;
          ">🎯</div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
      })

      const landingMarker = L.marker([landingLocation.latitude, landingLocation.longitude], { icon: landingIcon }).addTo(mapInstance)

      landingMarker.bindPopup(`
        <div style="min-width: 200px; font-family: system-ui, sans-serif;">
          <h3 style="margin: 0 0 10px 0; color: #059669; font-size: 16px; font-weight: 600;">
            ランディング
          </h3>
          <div style="font-size: 14px; color: #6b7280; line-height: 1.5;">
            <div><strong>緯度:</strong> ${landingLocation.latitude}°</div>
            <div><strong>経度:</strong> ${landingLocation.longitude}°</div>
            <div><strong>標高:</strong> ${landingLocation.elevation}m</div>
          </div>
        </div>
      `)

      // 2点を結ぶ線を描画
      L.polyline(
        [[takeoffLocation.latitude, takeoffLocation.longitude],
         [landingLocation.latitude, landingLocation.longitude]],
        {
          color: '#3b82f6',
          weight: 2,
          opacity: 0.6,
          dashArray: '5, 10'
        }
      ).addTo(mapInstance)

      // 地図の表示範囲を2つのマーカーが収まるように調整
      const bounds = L.latLngBounds([
        [takeoffLocation.latitude, takeoffLocation.longitude],
        [landingLocation.latitude, landingLocation.longitude]
      ])
      mapInstance.fitBounds(bounds, { padding: [50, 50] })
    }
    
    const destroyMap = () => {
      if (mapInstance) {
        mapInstance.remove()
        mapInstance = null
      }
    }

    // --- 過去の気象情報モード ---

    const toIsoDate = (value) => {
      const d = new Date(value)
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    }

    // 日付ピッカーの選択可能範囲は、取得できた日付そのものから決まる
    const historyMinDate = computed(() =>
      availableDates.value.length ? toIsoDate(availableDates.value[0]) : ''
    )
    const historyMaxDate = computed(() =>
      availableDates.value.length ? toIsoDate(availableDates.value[availableDates.value.length - 1]) : ''
    )

    // 過去モードの日付セルは琥珀色にして、未来モードと区別できるようにする
    const dateCellClass = (date) => {
      const base = 'px-3 py-3 text-center cursor-pointer border transition-colors min-w-[72px]'
      if (selectedDate.value === date) {
        return isHistoryMode.value
          ? `${base} date-cell-selected bg-amber-200 border-amber-500 font-semibold text-amber-900`
          : `${base} date-cell-selected bg-blue-100 border-blue-400 font-semibold text-blue-800`
      }
      return isHistoryMode.value
        ? `${base} bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100`
        : `${base} bg-white border-gray-200 hover:bg-blue-50`
    }

    // 選択セルが画面外にあるとき（全期間だと日付セルが多い）横スクロールで見せる
    const scrollSelectedDateIntoView = () => {
      nextTick(() => {
        const cell = historyDateRow.value?.querySelector('.date-cell-selected')
        if (cell) cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      })
    }

    // date を省略すると、APIが提供できる過去期間の全体を1リクエストで返す。
    // 範囲を事前に知る必要がないので availability の問い合わせは不要。
    const loadHistoryData = async () => {
      const areaId = isSampleMode.value ? route.params.id : route.params.areaId

      historyError.value = ''
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/app/history/areas/${areaId}/`
        )
        weatherData.value = response.data

        if (!availableDates.value.length) {
          historyError.value = 'この期間の過去データはまだありません。'
          selectedDate.value = ''
          return
        }

        const target = availableDates.value.find(d => toIsoDate(d) === historyDate.value)
        selectedDate.value = target || availableDates.value[availableDates.value.length - 1]
        historyDate.value = toIsoDate(selectedDate.value)
        selectDefaultTimeIndex()

        await nextTick()
        scrollSelectedDateIntoView()
        setTimeout(() => {
          createTimeSeriesChart()
          drawCloudColormap()
          updateAltitudeCharts()
        }, 150)
      } catch (error) {
        console.error('History data fetch failed:', error)
        historyError.value = error.response?.data?.error || '過去の気象情報の取得に失敗しました。'
        weatherData.value = null
      }
    }

    const enterHistoryMode = async () => {
      isRefreshing.value = true
      try {
        isHistoryMode.value = true
        await loadHistoryData()
      } finally {
        isRefreshing.value = false
      }
    }

    const exitHistoryMode = async () => {
      isRefreshing.value = true
      try {
        isHistoryMode.value = false
        historyError.value = ''
        await loadWeatherData()
      } finally {
        isRefreshing.value = false
      }
    }

    // 全期間を読み込み済みなので、日付ピッカーは再取得せず選択日を移すだけ
    const onHistoryDateChange = () => {
      if (!historyDate.value) return
      const target = availableDates.value.find(d => toIsoDate(d) === historyDate.value)
      if (!target) return
      selectDate(target)
      scrollSelectedDateIntoView()
    }

    // 手動更新機能
    const refreshWeatherData = async () => {
      isRefreshing.value = true
      try {
        if (isHistoryMode.value) {
          await loadHistoryData()
        } else {
          await loadWeatherData() // 最新のキャッシュデータを取得
        }
      } catch (error) {
        console.error('Weather data refresh failed:', error)
        alert('気象データの更新に失敗しました')
      } finally {
        isRefreshing.value = false
      }
    }

    // データの経過時間をチェックして自動更新
    const checkAndAutoRefresh = () => {
      if (isHistoryMode.value) return
      if (weatherData.value?.data_age_hours >= 3) {
        console.log('Data is older than 3 hours, auto-refreshing...')
        loadWeatherData()
      }
    }

    onMounted(async () => {
      await loadWeatherData()
      // 初回読み込み後、3時間経過していたら自動更新
      checkAndAutoRefresh()
    })
    
    onBeforeUnmount(() => {
      destroyMap()
    })
    
    // 天気データが読み込まれた後に地図を初期化
    watch(weatherData, (newData) => {
      if (newData && newData.location) {
        nextTick(() => {
          setTimeout(() => {
            initializeMap()
          }, 100)
        })
      }
    })

    return {
      isSampleMode,
      isEmbedMode,
      weatherData,
      selectedDate,
      selectedTimeIndex,
      selectedForecast,
      isLoading,
      isRefreshing,
      activeChartTab,
      availableDates,
      dailyForecast,
      chartTabs,
      pressureLevels,
      timeSeriesChart,
      emagramChart,
      hodographChart,
      cloudColormapCanvas,
      mapContainer,
      formatDateTime,
      formatModelRun,
      formatDate,
      formatTime,
      formatDateShort,
      formatDayOfWeek,
      selectDate,
      getDailyWeatherIcon,
      getDailyMaxTemp,
      getDailyMinTemp,
      reversedPressureLevels,
      getWeatherIcon,
      getWeatherCodeDescription,
      getWindDirection,
      getWindArrowStyle,
      getCloudLevel,
      filterForecastByDate,
      selectTimeForAltitudeData,
      previousTime,
      nextTime,
      updateAltitudeCharts,
      refreshWeatherData,
      isHistoryMode,
      historyMinDate,
      historyMaxDate,
      historyDate,
      historyError,
      historyDateRow,
      dateCellClass,
      enterHistoryMode,
      exitHistoryMode,
      onHistoryDateChange
    }
  }
}
</script>

<style scoped>
.wind-arrow {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 40px;
  margin: 2px;
}

.wind-arrow::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 10%;
  width: calc(60% * var(--arrow-size, 1));
  height: calc(6px * var(--arrow-size, 1));
  background-color: var(--arrow-color, #9CA3AF);
  transform: translateY(-50%);
  border-radius: 2px;
}

.wind-arrow::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 5%;
  width: 0;
  height: 0;
  border-left: calc(12px * var(--arrow-size, 1)) solid var(--arrow-color, #9CA3AF);
  border-top: calc(8px * var(--arrow-size, 1)) solid transparent;
  border-bottom: calc(8px * var(--arrow-size, 1)) solid transparent;
  transform: translateY(-50%);
}
.modern-table-container {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.modern-weather-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table-header {
  background: linear-gradient(to right, #eff6ff, #e0e7ff);
  border-bottom: 2px solid #3b82f6;
}

.table-header-cell {
  padding: 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e40af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-right: 1px solid #d1d5db;
}

.table-header-cell.border-r-0 {
  border-right: none;
}

.table-row {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  border-bottom: 1px solid #e5e7eb;
}

.table-row:hover {
  background-color: #eff6ff !important;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color: #f9fafb;
}

.row-selected {
  background-color: #dbeafe !important;
  border-left: 4px solid #3b82f6 !important;
  box-shadow: 0 0 0 1px #93c5fd;
}

.row-selected:hover {
  background-color: #dbeafe !important;
}

.selected-time-cell {
  background-color: #1e40af !important;
  color: #ffffff !important;
  border-radius: 0.375rem;
  font-weight: 700 !important;
}

.table-cell {
  padding: 1rem;
  color: #374151;
  border-right: 1px solid #e5e7eb;
}

.table-cell.border-r-0 {
  border-right: none;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-red {
  background-color: #fef2f2;
  color: #991b1b;
}

.badge-blue {
  background-color: #eff6ff;
  color: #1e40af;
}

.badge-indigo {
  background-color: #eef2ff;
  color: #3730a3;
}

.badge-purple {
  background-color: #faf5ff;
  color: #7c2d12;
}

.badge-yellow {
  background-color: #fefce8;
  color: #a16207;
}

.progress-bar {
  flex: 1;
  height: 0.5rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(to right, #9ca3af, #6b7280);
  border-radius: 9999px;
  transition: width 0.3s ease-in-out;
}

.emagram-container {
  width: 100%;
  height: 600px;
  max-width: 700px;
  margin: 0 auto;
  position: relative;
}

.hodograph-container {
  width: 100%;
  height: 500px;
  max-width: 500px;
  margin: 0 auto;
  position: relative;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .emagram-container {
    height: 400px;
    max-width: 100%;
  }
  
  .hodograph-container {
    height: 400px;
    max-width: 100%;
  }
}

.cloud-colormap-container {
  overflow-x: auto;
}
.cloud-colormap-container canvas {
  display: block;
}
</style>
